/**
 * The back office's menu areas, in the order the rail shows them.
 *
 * Five spaces, and the order is the argument: **Catalogue comes first**,
 * because the question the desk answers all day is "I look for an article,
 * I see its stock, I see its price". Then the customers, then what is sold
 * to them, then everything that configures the rest.
 *
 * - **Accueil** — the home page: a quick search and the context.
 * - **Catalogue** — the operational screens: consult articles, stock by
 *   depot, availability, enrichment, the referential's articles.
 * - **Clients** — the customers, their requests and their after-sales.
 * - **Ventes** — quotations, orders, invoices.
 * - **Administration** — the structure (companies, points of sale, depots),
 *   people and settings, the catalogue's configuration (the tree, ERP
 *   families, attributes, article setup) and the ERP connection.
 *
 * Destinations are named by path where the order matters and the feature's
 * group would not give it: the tree is configuration rather than something
 * consulted, so it sits under Administration with the ERP families it maps;
 * the referential's companies are there too while its articles list stays in
 * Catalogue. A path rule beats a group rule
 * (`arrangeNavigation`), so the groups below only catch what a feature adds
 * later — it appears in its space, unsorted, rather than nowhere.
 *
 * Apart from `navigation.tsx` so that file exports only components, which is
 * what keeps fast refresh working on it; the tests read the list from here.
 */

import type { ReactNode } from "react";
import { HOME_PATH, type NavArea } from "@hardauto/core-router";
import { CATALOG_PATHS } from "@hardauto/feature-catalog";
import { FILES_PATHS } from "@hardauto/feature-files";
import { ORDERS_PATHS } from "@hardauto/feature-orders";
import { REFERENTIAL_PATHS } from "@hardauto/feature-referential";

import { icons } from "./icons";

export interface BackOfficeArea extends NavArea {
  labelKey: string;
  icon: ReactNode;
}

/** In the order the rail shows them. */
export const NAV_AREAS: readonly BackOfficeArea[] = [
  {
    id: "home",
    labelKey: "backoffice:menu.home",
    icon: icons.home,
    include: [{ path: HOME_PATH }],
  },
  {
    id: "catalog",
    labelKey: "backoffice:menu.catalog",
    icon: icons.catalog,
    include: [
      { path: CATALOG_PATHS.consultation },
      { path: REFERENTIAL_PATHS.stockByDepot },
      { path: REFERENTIAL_PATHS.stock },
      { path: CATALOG_PATHS.availability },
      { path: CATALOG_PATHS.fiches },
      { path: REFERENTIAL_PATHS.articles },
      { group: "catalog:group" },
      { group: "referential:group" },
    ],
  },
  {
    id: "clients",
    labelKey: "backoffice:menu.clients",
    icon: icons.clients,
    include: [
      { group: "clients:group" },
      { group: "onboarding:group" },
      { group: "support:group" },
    ],
  },
  {
    id: "sales",
    labelKey: "backoffice:menu.sales",
    icon: icons.sales,
    include: [
      { path: ORDERS_PATHS.quotes },
      { path: ORDERS_PATHS.orders },
      { group: "billing:group" },
      { group: "orders:group" },
    ],
  },
  {
    id: "administration",
    labelKey: "backoffice:menu.administration",
    icon: icons.configuration,
    include: [
      { path: REFERENTIAL_PATHS.companies },
      { path: REFERENTIAL_PATHS.pointsOfSale },
      { path: REFERENTIAL_PATHS.depots },
      // Users, roles, settings, audit, the dashboard: the platform's own.
      { section: "admin" },
      // The catalogue's configuration, together: the tree, the ERP families
      // it maps, the attributes, the article setup.
      { path: CATALOG_PATHS.categories },
      { path: REFERENTIAL_PATHS.families },
      { path: CATALOG_PATHS.attributes },
      { path: CATALOG_PATHS.brands },
      { path: CATALOG_PATHS.articleSetup },
      { path: REFERENTIAL_PATHS.pricing },
      // The order policy is the orders feature's, and a setting rather than
      // a sales screen: by path, so the rest of its group stays in "Ventes".
      { path: ORDERS_PATHS.policy },
      { group: "integration:group" },
      { path: FILES_PATHS.library },
    ],
  },
];
